import { compileTrack } from './authoring/compile';
import { Engine, type EngineDom, type EngineTestHooks } from './core/Engine';
import type { EditorTestHooks } from './editor/EditorApp';
import { GameApp, type GameTestHooks } from './app/GameApp';
import { ProjectStore } from './editor/ProjectStore';
import { levelFromQuery } from './levels';
import { RendererInitError } from './rendering/Renderer';
import { loadSettings } from './ui/Settings';
import { applyLineColour } from './ui/theme';
import type { LevelData } from './world/LevelData';

declare global {
  interface Window {
    __xernous?: EngineTestHooks;
    __xernousEditor?: EditorTestHooks;
    __xernousGame?: GameTestHooks;
  }
}

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`Missing #${id} in index.html`);
  return e as T;
}

function showFatal(message: string): void {
  const overlay = document.getElementById('overlay');
  const status = document.getElementById('overlay-status');
  const app = document.getElementById('app');
  if (app) app.hidden = false;
  if (status) {
    status.textContent = message;
    status.classList.add('error');
  }
  if (overlay) overlay.hidden = false;
  document.body.dataset['engine'] = 'error';
}

function gameDom(): Omit<EngineDom, 'canvas'> {
  return {
    hud: el('hud'),
    flash: el('flash'),
    debug: el('debug'),
    overlay: el('overlay'),
    status: el('overlay-status'),
    resume: el<HTMLButtonElement>('resume'),
    experiments: el('experiments'),
    app: el('app'),
    inputs: {
      sensitivity: el<HTMLInputElement>('set-sens'),
      fov: el<HTMLInputElement>('set-fov'),
      scale: el<HTMLInputElement>('set-scale'),
      roll: el<HTMLInputElement>('set-roll'),
    },
  };
}

async function startEditor(): Promise<void> {
  document.body.dataset['mode'] = 'editor';
  document.title = 'Xernous Track Editor';
  const host = el('app');
  host.hidden = true;
  el('create-track').hidden = true;
  const root = el('editor');
  root.hidden = false;
  const { EditorApp } = await import('./editor/EditorApp');
  const editor = await EditorApp.create(root, { host, dom: gameDom(), exit: el<HTMLButtonElement>('exit-playtest') });
  if (import.meta.env.DEV) window.__xernousEditor = editor.testHooks();
  document.body.dataset['engine'] = 'ready';
}

/** `?track=<projectId>`: plays a saved track for real (records are kept, keyed by its fingerprint). */
async function loadSavedTrack(id: string): Promise<LevelData> {
  const store = await ProjectStore.open();
  try {
    const { doc } = await store.load(id);
    const r = compileTrack(doc);
    if (!r.level) throw new Error(`"${doc.title}" can't be played yet: ${r.errors[0]?.message ?? 'it does not compile'}`);
    return r.level;
  } finally {
    store.close();
  }
}

/** The game (Phase 5): menus, track selection, play, the editor, all in one page. */
function startGame(): void {
  document.body.dataset['mode'] = 'game';
  document.title = 'Xernous';
  el('app').hidden = true;
  const shell = el('shell');
  shell.hidden = false;
  const app = new GameApp({ shell, editor: el('editor'), legacyHost: el('app'), legacyDom: gameDom(), legacyExit: el<HTMLButtonElement>('exit-playtest') });
  if (import.meta.env.DEV) window.__xernousGame = app.testHooks();
  document.body.dataset['engine'] = 'ready';
}

async function main(): Promise<void> {
  const params = new URLSearchParams(location.search);
  // Every entry point (game, editor, developer levels) draws its interface in the player's outline colour.
  applyLineColour(loadSettings().outlineColor);
  if (params.has('editor')) return startEditor();
  // Developer entry points keep their Phase 1–4 behaviour: ?level=<key> and ?track=<project id>.
  if (!params.has('level') && !params.has('track')) return startGame();
  const trackId = params.get('track');
  const level = trackId ? await loadSavedTrack(trackId) : levelFromQuery(location.search);
  const fpsLimit = import.meta.env.DEV ? Number(params.get('fps') ?? 0) || 0 : 0;
  const engine = await Engine.create({ ...gameDom(), canvas: el<HTMLCanvasElement>('view') }, level, { fpsLimit });
  if (import.meta.env.DEV) window.__xernous = engine.testHooks();
  engine.start();
  document.body.dataset['engine'] = 'ready';
}

main().catch((err: unknown) => {
  console.error('Engine failed to start', err);
  if (err instanceof RendererInitError) showFatal(err.message);
  else showFatal(`The engine failed to start: ${err instanceof Error ? err.message : String(err)}. Reload the page to retry.`);
});
